import { useQuery } from '@tanstack/react-query';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  Pencil as EditOutlined,
  Eye as EyeOutlined,
  ClipboardCheck as FileDoneOutlined,
  FileSearch as FileSearchOutlined,
  ListChecks as FindingsOutlined,
  RotateCcwClock as HistoryOutlined,
  Inbox as InboxOutlined,
  ThumbsUp as LikeOutlined,
  MessageSquareText as MiaReviewOutlined,
  RotateCcw as RedoOutlined,
  RefreshCw as RetweetOutlined,
  Rocket as RocketOutlined,
  NotebookTabs as SnippetsOutlined,
  Tags as TagsOutlined,
  TriangleAlert as WarningOutlined,
} from 'lucide-react';
import { useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router';

import { MealPhotoQueueQualityAssuranceResponse } from '@/api/generated/MNT';
import { getQueuesForQa } from '@/apiClients/mpq';
import { Capabilities } from '@/auth-capabilities';
import { QueueSummaryIcons } from '@/components/QueueSummaryIcons';
import { UserPresenceStateBadge } from '@/components/UserPresenceStateBadge';
import { config } from '@/config';
import { useAuth } from '@/context/appContext';
import { useFeatures } from '@/context/FeatureContext';
import { useAppUpdateService } from '@/services/AppUpdateService';
import { alertDialog } from '@/services/DialogService';
import { useMealQueueService } from '@/services/MealQueueService';
import { useMenuBar } from '@/services/MenuBarService';
import { useUserQualityAssuranceAvailableCount } from '@/services/userQualityAssuranceCount';
import { NavItemType } from '@/types/menu';

import { navItemCheckAuth } from './MainLayout/Drawer/DrawerContent/Navigation';
import { NavItem, NavItemProps } from './MainLayout/Drawer/DrawerContent/Navigation/NavItem';

const useCountAllQAItems = (opts?: { disabled?: boolean }) => {
  const { authInfo } = useAuth();
  const highPrioNotReviewedQAQuery = useQuery<MealPhotoQueueQualityAssuranceResponse[]>({
    queryKey: ['qa-item-list-prio-not-reviewed'],
    queryFn: async () => {
      if (config.EMERGENCY_THROTTLE_QA) {
        return [];
      }
      return getQueuesForQa(authInfo!.access_token, {
        data_reviewer_id: authInfo!.reviewer_id,
        is_priority: 'true',
        is_reviewed: 'false',
        limit: 20,
      });
    },
    refetchInterval: 60 * 1000,
    enabled: !opts?.disabled,
  });

  const highPrioEscalatedNoCustomQAQuery = useQuery<MealPhotoQueueQualityAssuranceResponse[]>({
    queryKey: ['qa-item-list-prio-escalated-no-custom'],
    queryFn: async () => {
      if (config.EMERGENCY_THROTTLE_QA) {
        return [];
      }
      return getQueuesForQa(authInfo!.access_token, {
        data_reviewer_id: authInfo!.reviewer_id,
        is_priority: 'true',
        is_escalated: 'true',
        has_custom_items: 'false',
        limit: 20,
      });
    },
    refetchInterval: 60 * 1000,
    enabled: !opts?.disabled,
  });

  return {
    numQAItems: (highPrioNotReviewedQAQuery.data?.length ?? 0) + (highPrioEscalatedNoCustomQAQuery.data?.length ?? 0),
    isSuccess: highPrioNotReviewedQAQuery.isSuccess && highPrioEscalatedNoCustomQAQuery.isSuccess,
  };
};

// ==============================|| MENU ITEMS ||============================== //

const UpdateAppMenuItem = (props: NavItemProps) => {
  const { updateAvailable } = useAppUpdateService();

  const reload = () => {
    if (!updateAvailable) {
      return;
    }

    const queryParams = new URLSearchParams(window.location.search);
    queryParams.set('target-version', updateAvailable);
    window.location.href = '?' + queryParams.toString();
  };

  if (config.IS_PREVIEW) {
    return (
      <NavItem
        level={props.level}
        item={{
          id: 'build-time',
          type: 'item',
          disabled: false,
          onClick: () => {
            void alertDialog(
              'This preview build contains new and experimental features. Please report any issues to the development team.',
              { title: 'Preview build' },
            );
          },
          title: config.RELEASE_LABEL,
          chip: {
            label: 'preview',
            color: 'brand',
          },
        }}
      />
    );
  }

  if (updateAvailable) {
    return (
      <NavItem
        level={props.level}
        item={{
          id: 'build-time',
          type: 'item',
          title: 'Update App',
          icon: RetweetOutlined,
          onClick: reload,
          chip: {
            label: 'Up',
            color: 'caution',
          },
        }}
      />
    );
  }

  return (
    <NavItem
      level={props.level}
      item={{
        id: 'build-time',
        type: 'item',
        disabled: true,
        title: config.RELEASE_LABEL,
      }}
    />
  );
};

const QueueMenuItem = (props: NavItemProps) => {
  const { queue_polling: queuePolling } = useFeatures();
  if (!queuePolling) {
    return null;
  }
  return <QueueMenuItemInner {...props} />;
};

const QueueMenuItemInner = (props: NavItemProps) => {
  const mpq = useMealQueueService();
  const drawerOpen = useMenuBar(state => state.drawerOpen);

  const ownedQueues = mpq.ownedQueues;
  const otherQueues = mpq.allActiveQueues.filter(q => !ownedQueues.find(oq => oq.id === q.id));
  const queuesToShow = [...ownedQueues, ...otherQueues];

  const shouldShowChip = !mpq.currentlyLabellingOwnQueue && (mpq.ownedCount || mpq.availableCount);

  return (
    <>
      <NavItem
        level={props.level}
        item={{
          // The menu item id is `queue`. Without it, `/` marks that id active on
          // the hidden wrapper while this row stays unselected, so the highlight
          // remains on the last open queue.
          id: props.item.id,
          title: 'Queue',
          url: '/',
          icon: InboxOutlined,
          chip: !shouldShowChip
            ? null
            : {
                label: '' + (mpq.ownedCount || mpq.availableCount),
                color: 'critical',
                size: 'small',
              },
        }}
      />
      {drawerOpen && queuesToShow.length > 0 && <QueueMenuRows queues={queuesToShow} level={props.level + 1} />}
    </>
  );
};

const QUEUE_ROW_HEIGHT = 38;

/**
 * The reviewer's queue rows. Only the rows in view are mounted: each one
 * subscribes to presence and queue state, so rendering a hundred at once made
 * every update in the sidebar expensive.
 */
const QueueMenuRows = (props: { queues: ReturnType<typeof useMealQueueService>['allActiveQueues']; level: number }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: props.queues.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => QUEUE_ROW_HEIGHT,
    overscan: 4,
  });

  // Keep the open queue's row mounted and in view so it can mark itself active.
  const { pathname } = useLocation();
  const activeIndex = props.queues.findIndex(queue => pathname.endsWith('/' + queue.id));
  // biome-ignore lint/correctness/useExhaustiveDependencies: carried over from the former ESLint suppression
  useEffect(() => {
    if (activeIndex >= 0) {
      virtualizer.scrollToIndex(activeIndex, { align: 'auto' });
    }
  }, [activeIndex]);

  return (
    <div ref={scrollRef} className="scrollbar-sidebar max-h-72 overflow-x-hidden overflow-y-auto">
      <div className="relative w-full" style={{ height: virtualizer.getTotalSize() }}>
        {virtualizer.getVirtualItems().map(row => {
          const queue = props.queues[row.index];
          return (
            <div
              key={queue.id}
              className="absolute inset-x-0 top-0"
              style={{ height: row.size, transform: `translateY(${row.start}px)` }}
            >
              <NavItem
                level={props.level}
                item={{
                  id: 'queue-' + queue.id,
                  title: (
                    // Inline presence slot (not absolute): left-nav-1 matched the
                    // row's pl-nav-1, so the dot sat on top of the patient/queue id.
                    // Fixed size-2 slot keeps offline rows aligned when the badge hides.
                    <span
                      className="inline-flex min-w-0 max-w-full items-center gap-1.5"
                      title={`${queue.patient_id}/${queue.id}`}
                    >
                      <span className="inline-flex size-2 shrink-0 items-center justify-center">
                        <UserPresenceStateBadge userId={queue.patient_id} hideOffline />
                      </span>
                      <span className="truncate">
                        {queue.patient_id}/{queue.id}
                      </span>
                    </span>
                  ),
                  url: '/queue-item/' + queue.id + '?utm_source=navbar',

                  iconRight: <QueueSummaryIcons queue={queue} dense />,
                }}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

const InitialQAMenuItem = (props: NavItemProps) => {
  const auth = useAuth();
  const qaAvailable = useUserQualityAssuranceAvailableCount({
    disabled: !auth.hasAuth(Capabilities.commonDataReviewerAdmin),
  });

  const toReviewItemsLength = !auth.hasAuth(Capabilities.commonDataReviewerAdmin)
    ? 0
    : qaAvailable.numQA2AvailableQueues;

  return (
    <NavItem
      level={props.level}
      item={{
        // Must match the menu item id (`initial-qa`). `qa` collides with the
        // parent group id, so the active state never sticks on this row.
        id: props.item.id ?? 'initial-qa',
        title: 'Initial QA',
        url: '/qa/initial-qa',
        icon: RocketOutlined,
        chip: !toReviewItemsLength
          ? null
          : {
              label: `${toReviewItemsLength >= 20 ? '20+' : toReviewItemsLength}`,
              color: 'caution',
              size: 'small',
            },
      }}
    />
  );
};

const AllQueuesMenuItem = (props: NavItemProps) => {
  const { authInfo } = useAuth();
  const { numQAItems } = useCountAllQAItems({
    disabled: !authInfo?.is_data_reviewer_admin,
  });

  const toReviewItemsLength = !authInfo?.is_data_reviewer_admin ? 0 : numQAItems;

  return (
    <NavItem
      level={props.level}
      item={{
        id: 'all-qa',
        title: 'All Queues',
        url: '/qa',
        icon: EyeOutlined,
        chip: !toReviewItemsLength
          ? null
          : {
              label: `${toReviewItemsLength >= 20 ? '20+' : toReviewItemsLength}`,
              color: 'critical',
              size: 'small',
            },
      }}
    />
  );
};

const MyFoodsPage = (props: NavItemProps) => {
  return (
    <NavItem
      level={props.level}
      item={{
        id: 'my-foods',
        title: 'My Foods',
        url: '/foods/table/my-foods?food-database-table-sort=%5B%5D&food-database-table-filter=%7B%22is_associated_with_me%22%3Atrue%7D',
      }}
    />
  );
};

const NAVBAR_MENU_ITEMS: { items: NavItemType[] } = {
  items: [
    {
      id: '',
      title: '',
      type: 'group',
      children: [
        {
          id: 'queue',
          type: 'item',
          // Title and url feed the page heading; the row itself is drawn by `render`.
          title: 'Queue',
          url: '/',
          auth: Capabilities.mpqItemView,
          render: props => <QueueMenuItem {...props} />,
        },
        {
          id: 'annotation',
          title: 'Annotation',
          type: 'collapse',
          icon: TagsOutlined,
          // Phase 1 IA placeholder — no routes yet.
          children: [
            {
              id: 'annotation-home',
              title: 'Annotation home',
              type: 'item',
              disabled: true,
              caption: 'Coming soon',
            },
            {
              id: 'annotation-gold-set-queue',
              title: 'Gold-set queue',
              type: 'item',
              disabled: true,
              caption: 'Coming soon',
            },
            {
              id: 'annotation-food-mappings',
              title: 'Food mappings',
              type: 'item',
              disabled: true,
              caption: 'Coming soon',
            },
            {
              id: 'annotation-gold-set-explorer',
              title: 'Gold set explorer',
              type: 'item',
              disabled: true,
              caption: 'Coming soon',
            },
            {
              id: 'annotation-taxonomy',
              title: 'Taxonomy',
              type: 'item',
              disabled: true,
              caption: 'Coming soon',
            },
          ],
        },
        {
          id: 'food-search',
          title: 'Foods',
          type: 'collapse',
          icon: FileSearchOutlined,
          auth: Capabilities.foodDbView,
          children: [
            {
              id: 'food-search',
              title: 'Food Search',
              type: 'item',
              url: '/foods',
            },
            {
              id: 'all-foods',
              title: 'All Foods',
              type: 'item',
              url: '/foods/table',
            },
            {
              id: 'custom-triage-foods',
              title: 'Custom Items',
              type: 'item',
              url: '/foods/table/custom?food-database-table-sort=[{"field"%3A"created_time"%2C"direction"%3A"asc"}]&food-database-table-filter={"status"%3A{"eq"%3A"custom_meal_item"}}',
            },
            {
              id: 'triaged-foods',
              title: 'Triaged Foods',
              type: 'item',
              url: '/foods/table/triaged?food-database-table-sort=[{"field"%3A"created_time"%2C"direction"%3A"asc"}]&food-database-table-filter={"status"%3A{"eq"%3A"triaged"}%2C"option_value_preparation_method"%3A{"ne"%3A"recipe"}}',
            },
            {
              id: 'triaged-recipes',
              title: 'Triaged Recipes',
              type: 'item',
              url: '/foods/table/triaged-recipes?food-database-table-sort=[{"field"%3A"created_time"%2C"direction"%3A"asc"}]&food-database-table-filter={"status"%3A{"eq"%3A"triaged"}%2C"option_value_preparation_method"%3A{"eq"%3A"recipe"}}',
            },
            {
              id: 'triaged-cpg',
              title: 'Triaged CPGs',
              type: 'item',
              url: '/foods/table/triaged-cpg?food-database-table-sort=[{"field"%3A"created_time"%2C"direction"%3A"asc"}]&food-database-table-filter={"status"%3A{"eq"%3A"triaged"}%2C"option_value_preparation_method"%3A{"eq"%3A"cpg"}}',
            },
            {
              id: 'draft-foods',
              title: 'Draft Foods',
              type: 'item',
              url: '/foods/table/draft?food-database-table-sort=[{"field"%3A"created_time"%2C"direction"%3A"asc"}]&food-database-table-filter={"status"%3A{"eq"%3A"draft"}}',
            },
            {
              id: 'review-foods',
              title: 'Ready for Review',
              type: 'item',
              url: '/foods/table/review?food-database-table-sort=[{"field"%3A"created_time"%2C"direction"%3A"asc"}]&food-database-table-filter={"status"%3A{"eq"%3A"under_review"}}',
            },
            {
              id: 'my-foods',
              title: 'My Foods',
              type: 'item',
              url: '/foods/table/my-foods?food-database-table-sort=%5B%5D&food-database-table-filter=%7B%22is_associated_with_me%22%3Atrue%7D',
              render: props => <MyFoodsPage {...props} />,
            },
            {
              id: 'brands',
              title: 'Brands',
              type: 'item',
              auth: Capabilities.brandManage,
              url: '/brands',
            },
            {
              id: 'restaurant-menus',
              title: 'Restaurant Menus',
              type: 'item',
              auth: Capabilities.foodDbView,
              url: '/restaurant-menus',
            },
            {
              id: 'grounding-requests',
              title: 'Grounding Requests',
              type: 'item',
              auth: Capabilities.foodDbView,
              url: '/grounding/requests',
            },
          ],
        },
        {
          id: 'recipes',
          title: 'Recipes',
          type: 'collapse',
          icon: SnippetsOutlined,
          auth: Capabilities.foodDbView,
          children: [
            {
              id: 'all-recipes',
              title: 'All Recipes',
              type: 'item',
              url: '/recipes',
            },
            {
              // A scanned or imported recipe lands as a draft that cannot publish
              // until its ingredients are resolved. Without an entry point the
              // curator has to remember it exists.
              id: 'recipes-needs-attention',
              title: 'Recipes to finish',
              type: 'item',
              url: '/recipes/needs-attention',
            },
            {
              id: 'recipes-import',
              title: 'Import',
              type: 'item',
              url: '/recipes/import',
            },
            {
              id: 'recipes-usage',
              title: 'Recipe usage',
              type: 'item',
              url: '/recipes/usage',
            },
          ],
        },
      ],
    },
    {
      id: 'qa',
      title: 'QA',
      type: 'group',
      children: [
        {
          id: 'initial-qa',
          title: 'Initial QA',
          type: 'item',
          auth: Capabilities.mpqQAView,
          url: '/qa/initial-qa',
          render: props => <InitialQAMenuItem {...props} />,
        },
        {
          id: 'all-qa',
          title: 'All Queues',
          type: 'item',
          url: '/qa',
          auth: Capabilities.mpqItemView,
          render: props => <AllQueuesMenuItem {...props} />,
        },
        {
          id: 'feedback',
          title: 'Feedback',
          type: 'item',
          url: '/feedback',
          auth: Capabilities.mpqQAView,
          icon: LikeOutlined,
        },
      ],
    },
    {
      id: 'review',
      title: 'Review',
      type: 'group',
      children: [
        {
          id: 'review',
          title: 'Queues',
          type: 'item',
          auth: Capabilities.mpqItemView,
          url: '/review',
          icon: RedoOutlined,
        },
        {
          id: 'findings',
          title: 'Findings',
          type: 'item',
          auth: Capabilities.mpqQAView,
          feature: 'findings_inbox',
          url: '/findings',
          matchNested: true,
          icon: FindingsOutlined,
        },
        {
          id: 'mia-review',
          title: 'Mia review',
          type: 'item',
          auth: Capabilities.mpqQAView,
          feature: 'mia_review',
          url: '/mia-review',
          matchNested: true,
          icon: MiaReviewOutlined,
        },
        {
          id: 'custom-meal-items',
          title: 'Custom Meal Items',
          type: 'item',
          auth: Capabilities.commonDataReviewerAdmin,
          url: '/custom-meal-items',
          icon: EditOutlined,
        },
        {
          id: 'report-review',
          title: 'Patient Reports',
          type: 'item',
          auth: Capabilities.patientReportView,
          url: '/patient-reports',
          icon: FileDoneOutlined,
        },
        {
          id: 'meal-gallery',
          title: 'Meal Gallery',
          type: 'item',
          url: '/meals/browse',
          icon: EyeOutlined,
        },
      ],
    },
    {
      id: 'development-and-training',
      title: 'Development & Training',
      type: 'group',
      children: [
        {
          id: 'replay-logs',
          title: 'Replay Logs',
          type: 'item',
          auth: Capabilities.trainingLogReplay,
          url: '/dev/replaylogs',
          icon: HistoryOutlined,
        },
      ],
    },
    {
      id: 'support',
      title: 'Support',
      type: 'group',
      children: [
        {
          id: 'support-meal-trace',
          title: 'Meal Trace',
          type: 'item',
          auth: Capabilities.supportTraceView,
          url: '/support/meal-trace',
          icon: WarningOutlined,
        },
      ],
    },
    {
      id: 'build-info',
      title: 'Build Info',
      type: 'group',
      children: [
        {
          id: 'build-time',
          type: 'item',
          render: props => <UpdateAppMenuItem {...props} />,
        },
      ],
    },
  ],
};

export const useNavbarMenuItems = () => {
  const { hasAuth } = useAuth();
  const features = useFeatures();

  return useMemo(() => {
    const filterItem = (item: NavItemType): NavItemType | null => {
      if (!navItemCheckAuth(item, hasAuth)) {
        return null;
      }
      if (item.feature && !features[item.feature]) {
        return null;
      }

      const children = (item.children?.map(filterItem).filter(x => !!x) as NavItemType[]) ?? null;
      if (item.children?.length && !children?.length) {
        return null;
      }

      return {
        ...item,
        children,
      };
    };
    return {
      ...NAVBAR_MENU_ITEMS,
      items: NAVBAR_MENU_ITEMS.items.map(filterItem).filter(x => !!x) as NavItemType[],
    };
  }, [hasAuth, features]);
};
